import { cn } from "@/lib/utils";
import type {
  ExperimentKind,
  ExperimentStatus,
  JournalEntryType,
} from "@/lib/types";
import {
  Activity,
  Check,
  CircleDashed,
  CircleX,
  FlaskConical,
  GitBranch,
  Lightbulb,
  NotebookPen,
  Search,
  Settings2,
  SlidersHorizontal,
  TestTube2,
  X,
} from "lucide-react";

/* ─── Experiment status ────────────────────────────────────────────────── */

const statusStyles: Record<
  ExperimentStatus,
  { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }
> = {
  running: {
    label: "running",
    cls: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    icon: Activity,
  },
  completed: {
    label: "completed",
    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    icon: Check,
  },
  failed: {
    label: "failed",
    cls: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    icon: CircleX,
  },
  cancelled: {
    label: "cancelled",
    cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
    icon: X,
  },
  pending: {
    label: "pending",
    cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
    icon: CircleDashed,
  },
};

export function StatusBadge({
  status,
  className,
}: {
  status: ExperimentStatus;
  className?: string;
}) {
  const s = statusStyles[status];
  const Icon = s.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        s.cls,
        status === "running" && "animate-pulse",
        className,
      )}
    >
      <Icon className="h-3 w-3" />
      {s.label}
    </span>
  );
}

/* ─── Experiment kind ──────────────────────────────────────────────────── */

const kindStyles: Record<
  ExperimentKind,
  { cls: string; icon: React.ComponentType<{ className?: string }> }
> = {
  evolution: {
    cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
    icon: GitBranch,
  },
  trial: {
    cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
    icon: TestTube2,
  },
  probe: {
    cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
    icon: Search,
  },
  sweep: {
    cls: "bg-teal-500/15 text-teal-300 border-teal-500/40",
    icon: SlidersHorizontal,
  },
};

export function KindBadge({
  kind,
  className,
}: {
  kind: ExperimentKind;
  className?: string;
}) {
  const s = kindStyles[kind] ?? kindStyles.evolution;
  const Icon = s.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium",
        s.cls,
        className,
      )}
    >
      <Icon className="h-3 w-3" />
      {kind}
    </span>
  );
}

/* ─── Journal entry type ───────────────────────────────────────────────── */

const entryTypeStyles: Record<
  JournalEntryType,
  { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }
> = {
  observation: {
    label: "observation",
    cls: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    icon: NotebookPen,
  },
  finding: {
    label: "finding",
    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    icon: FlaskConical,
  },
  hypothesis: {
    label: "hypothesis",
    cls: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    icon: Lightbulb,
  },
  failure_diagnosis: {
    label: "failure diagnosis",
    cls: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    icon: CircleX,
  },
  design_decision: {
    label: "design decision",
    cls: "bg-violet-500/15 text-violet-300 border-violet-500/40",
    icon: Settings2,
  },
  methodology_note: {
    label: "methodology",
    cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
    icon: NotebookPen,
  },
};

export function EntryTypeBadge({
  entryType,
  className,
}: {
  entryType: JournalEntryType;
  className?: string;
}) {
  const s =
    entryTypeStyles[entryType] ?? {
      label: entryType,
      cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
      icon: NotebookPen,
    };
  const Icon = s.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        s.cls,
        className,
      )}
    >
      <Icon className="h-3 w-3" />
      {s.label}
    </span>
  );
}

/** Accent color class for a journal entry type — used for borders, text. */
export function entryTypeAccent(entryType: JournalEntryType): string {
  return (
    entryTypeStyles[entryType]?.cls.split(" ")[1] ?? "text-muted-foreground"
  );
}
